import { icon } from "../lib/icons";

export function showcaseHTML(): string {
  return `
<div class="showcase" x-data="showcase()">
  <header class="hero">
    <div class="hero-inner">
      <div class="hero-copy">
        <div class="eyebrow">
          <span class="dot-on"></span> Operations software · Built for flight schools
        </div>
        <h1>The dispatch board your<br/>spreadsheet pretends to be.</h1>
        <p class="lede">
          Runway Sync replaces the calendar-plus-clipboard mess at small and mid-size flight
          schools with one operations console: live schedule, conflict catching, dispatch
          readiness, and student progress, visible at a glance.
        </p>
        <div class="hero-ctas">
          <button class="btn btn-primary" @click="$store.app.enterApp()">
            Open the live demo ${icon("arrowR", 14)}
          </button>
          <a href="#early-access" class="btn">Request early access</a>
        </div>
        <div class="hero-meta mono">
          <span><span class="dim">v</span> 0.4.0-mvp</span>
          <span><span class="dim">stack</span> live · seeded</span>
          <span><span class="dim">sample</span> Skyhawk Aviation · VOBG · DGCA FTO</span>
        </div>
      </div>
      ${opsSnapshotHTML()}
    </div>
  </header>

  <section class="pillars">
    <div class="pillars-inner">
      ${pillarsHTML()}
    </div>
  </section>

  <section id="early-access" class="early-access">
    <div class="ea-inner">
      <div class="ea-copy">
        <div class="eyebrow"><span class="dot-on"></span> Early access</div>
        <h2>Get on the pilot list.</h2>
        <p class="lede">
          We're onboarding ten flight schools this quarter. Tell us a little about your
          operation and we'll set up a working demo against your real schedule.
        </p>
        <ul class="ea-bullets">
          <li>${icon("check", 14)} No credit card, no contract</li>
          <li>${icon("check", 14)} 30-minute migration from spreadsheets</li>
          <li>${icon("check", 14)} Direct line to the founding team</li>
        </ul>
      </div>

      <div class="card ea-card">
        <template x-if="!submitted">
          <form @submit.prevent="submit">
            <div class="card-h">
              <h3>Request early access</h3>
              <span class="muted">~ 60 seconds</span>
            </div>
            <div style="padding: 16px">
              <div class="grid-2">
                <div class="field">
                  <label class="field-label">Your name</label>
                  <input class="input" x-model="form.name" placeholder="Aarav Kapoor" />
                  <div class="field-error" x-show="errors.name" x-text="errors.name"></div>
                </div>
                <div class="field">
                  <label class="field-label">Work email</label>
                  <input class="input" x-model="form.email" placeholder="aarav@skyhawkaviation.in" />
                  <div class="field-error" x-show="errors.email" x-text="errors.email"></div>
                </div>
              </div>
              <div class="field">
                <label class="field-label">School name</label>
                <input class="input" x-model="form.school" placeholder="Skyhawk Aviation Academy" />
                <div class="field-error" x-show="errors.school" x-text="errors.school"></div>
              </div>
              <div class="grid-2">
                <div class="field">
                  <label class="field-label">Aircraft in fleet</label>
                  <select class="select" x-model="form.aircraftCount">
                    <option value="">Select</option>
                    <option>1–3</option>
                    <option>4–8</option>
                    <option>9–15</option>
                    <option>16+</option>
                  </select>
                  <div class="field-error" x-show="errors.aircraftCount" x-text="errors.aircraftCount"></div>
                </div>
                <div class="field">
                  <label class="field-label">Current scheduling method</label>
                  <select class="select" x-model="form.currentMethod">
                    <option>Spreadsheet</option>
                    <option>Google Calendar</option>
                    <option>Flight Circle</option>
                    <option>FlightLogger</option>
                    <option>Paper binder</option>
                    <option>Custom system</option>
                  </select>
                </div>
              </div>
              <div class="field">
                <label class="field-label">Biggest operations pain point</label>
                <textarea class="textarea" rows="3" x-model="form.painPoint"
                  placeholder="e.g. double-booked aircraft, expired medicals slipping through, no visibility into student progress…"></textarea>
              </div>
            </div>
            <div class="card-foot">
              <span class="dim mono" style="font-size: 11px">
                Stored securely · we'll only email about your demo
              </span>
              <button type="submit" class="btn btn-primary" :disabled="busy">
                <span x-show="!busy">Request access ${icon("send", 13)}</span>
                <span x-show="busy">Sending…</span>
              </button>
            </div>
          </form>
        </template>
        <template x-if="submitted">
          <div style="padding: 28px; text-align: center">
            <div class="success-ring">${icon("check", 28, 2.4, "white")}</div>
            <h3 style="margin: 16px 0 6px; font-size: 18px">You're on the list.</h3>
            <p class="muted" style="max-width: 360px; margin: 0 auto 16px">
              Thanks <span x-text="submitted.name.split(' ')[0]"></span>. We'll reach out at
              <span class="mono" style="color: var(--ink-800)" x-text="submitted.email"></span>
              to set up a tailored demo for <strong x-text="submitted.school"></strong>.
            </p>
            <button class="btn btn-primary" @click="$store.app.enterApp()">
              Explore the live demo while you wait ${icon("arrowR", 13)}
            </button>
          </div>
        </template>
      </div>
    </div>
  </section>

  <footer class="foot">
    <div class="foot-inner">
      <span class="brand">
        <span class="brand-mark">${icon("plane", 12, 1.6, "white")}</span>
        Runway Sync<small>OPS</small>
      </span>
      <span class="dim mono" style="font-size: 11px">
        Showcase build · all data simulated · no flights were harmed
      </span>
    </div>
  </footer>
</div>
`;
}

function pillarsHTML(): string {
  const pillars = [
    { tone: "brand", ico: icon("calendar", 20), title: "Live schedule", body: "Aircraft, instructors, students and lessons in one view. Filter by tail or instructor. See what's flying right now." },
    { tone: "warn",  ico: icon("warn", 20),     title: "Catches problems", body: "Double bookings hard-blocked. Missing medicals, instructor conflicts, and maintenance-due flags surface before dispatch." },
    { tone: "cyan",  ico: icon("tower", 20),    title: "Dispatch readiness", body: "An ops board that mirrors how dispatchers actually work: scheduled, ready, dispatched, airborne, returned, closed." },
    { tone: "ok",    ico: icon("grad", 20),     title: "Student progress", body: "Track lessons completed, blockers, and the next recommended action. Spot students stalling before they churn." },
  ];
  return pillars.map(p => `
    <div class="pillar">
      <div class="pillar-ico tone-${p.tone}">${p.ico}</div>
      <h3>${p.title}</h3>
      <p>${p.body}</p>
    </div>
  `).join("");
}

function opsSnapshotHTML(): string {
  const rows = [
    { tail: "VT-SAA", model: "C172", blocks: [{ s: 0, w: 26, kind: "warn", label: "R. Sharma · Pre-solo" }] },
    { tail: "VT-SAB", model: "PA-28", blocks: [{ s: 30, w: 22, kind: "warn", label: "A. Desai · Pattern" }] },
    { tail: "VT-SAC", model: "DA40", blocks: [{ s: 75, w: 20, kind: "live", label: "R. Sharma · Solo · airborne" }] },
  ];
  const rowHTML = rows.map(r => `
    <div class="snap-row">
      <div class="snap-tail">
        <span class="mono">${r.tail}</span>
        <span class="dim mono" style="font-size: 10px">${r.model}</span>
      </div>
      <div class="snap-track">
        ${r.blocks.map(b => `<div class="snap-block ${b.kind}" style="left: ${b.s}%; width: ${b.w}%">${b.label}</div>`).join("")}
      </div>
    </div>
  `).join("");

  return `
    <div class="snapshot">
      <div class="snap-frame">
        <div class="snap-titlebar">
          <span class="snap-dots"><span></span><span></span><span></span></span>
          <span class="mono">skyhawk-aviation · today · 14:08 IST</span>
          <span class="live-pulse" style="margin-left: auto">
            <span class="dot"></span> Live
          </span>
        </div>
        <div class="snap-body">
          <div class="snap-rail">
            <div class="snap-rail-h">
              <span class="mono dim">RAMP</span>
              <span class="mono dim">08</span>
              <span class="mono dim">10</span>
              <span class="mono dim">12</span>
              <span class="mono dim">14</span>
              <span class="mono dim">16</span>
              <span class="now-line" style="left: 62%"></span>
            </div>
            ${rowHTML}
          </div>
          <div class="snap-side">
            <div class="snap-card">
              <div class="snap-card-h">${icon("warn", 13)}<span>3 active warnings</span></div>
              <div class="snap-warn">
                <span class="chip warning"><span class="chip-dot"></span> medical</span>
                <span>R. Sharma · DGCA Class 2 missing</span>
              </div>
              <div class="snap-warn">
                <span class="chip warning"><span class="chip-dot"></span> maint</span>
                <span>VT-SAB · CAR-M insp in 3 days</span>
              </div>
              <div class="snap-warn">
                <span class="chip warning"><span class="chip-dot"></span> avail</span>
                <span>Capt. N. Rao · 13:00–15:00</span>
              </div>
            </div>
            <div class="snap-card">
              <div class="snap-card-h">${icon("tower", 13)}<span>Dispatch · now</span></div>
              <div class="snap-disp">
                <span class="mono">VT-SAC</span>
                <span class="chip airborne"><span class="chip-dot"></span>airborne</span>
              </div>
              <div class="snap-disp">
                <span class="mono">VT-SAA</span>
                <span class="chip ready"><span class="chip-dot"></span>ready</span>
              </div>
              <div class="snap-disp">
                <span class="mono">VT-SAB</span>
                <span class="chip warning"><span class="chip-dot"></span>not ready</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

interface ShowcaseForm {
  name: string;
  email: string;
  school: string;
  aircraftCount: string;
  currentMethod: string;
  painPoint: string;
}

interface ShowcaseState {
  form: ShowcaseForm;
  errors: Partial<Record<keyof ShowcaseForm, string>>;
  submitted: ShowcaseForm | null;
  busy: boolean;
  submit(): Promise<void>;
}

export function showcaseAlpine(): ShowcaseState {
  return {
    form: { name: "", email: "", school: "", aircraftCount: "", currentMethod: "Spreadsheet", painPoint: "" },
    errors: {},
    submitted: null,
    busy: false,
    async submit() {
      const errs: Partial<Record<keyof ShowcaseForm, string>> = {};
      if (!this.form.name.trim()) errs.name = "Required";
      if (!/^[^@]+@[^@]+\.[^@]+$/.test(this.form.email)) errs.email = "Valid email required";
      if (!this.form.school.trim()) errs.school = "Required";
      if (!this.form.aircraftCount) errs.aircraftCount = "Required";
      this.errors = errs;
      if (Object.keys(errs).length) return;

      this.busy = true;
      try {
        const res = await fetch("/api/lead", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(this.form),
        });
        if (!res.ok && res.status !== 0) {
          console.warn("[Runway Sync] /api/lead returned", res.status);
        }
      } catch (err) {
        console.warn("[Runway Sync] /api/lead failed (likely no backend in dev):", err);
      } finally {
        this.busy = false;
        this.submitted = { ...this.form };
        try {
          const leads = JSON.parse(localStorage.getItem("runway-sync.leads") || "[]");
          leads.push({ ...this.form, ts: new Date().toISOString() });
          localStorage.setItem("runway-sync.leads", JSON.stringify(leads));
        } catch (err) {
          console.warn("[Runway Sync] localStorage write failed:", err);
        }
      }
    },
  };
}
